import { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Button, Grid, FormControl, InputLabel, Select, MenuItem,
    Alert, IconButton, InputAdornment, Typography, Box, Collapse, CircularProgress
} from '@mui/material';
import { Visibility, VisibilityOff, AddCircle, RemoveCircle } from '@mui/icons-material';
import axios from 'axios';

const EditClientForm = ({ open, onClose, onSuccess, initialValues, banques }) => {
    const [formData, setFormData] = useState({ ...initialValues });
    const [showTejPassword, setShowTejPassword] = useState(false);
    const [showBanquePasswords, setShowBanquePasswords] = useState({});
    const [serverError, setServerError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && initialValues) {
            setFormData({
                ...initialValues,
                comptesBancaires: initialValues.comptesBancaires && initialValues.comptesBancaires.length > 0
                    ? initialValues.comptesBancaires
                    : [{ rib: '', banqueLogin: '', banquePassword: '', banque: '' }]
            });
            setServerError('');
            setShowTejPassword(false);
            setShowBanquePasswords({});
        }
    }, [open, initialValues]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        let processedValue = value;
        
        // Convertir codeClient en majuscules
        if (name === 'codeClient') {
            processedValue = value.toUpperCase();
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: processedValue
        }));
    };

    const handleCompteChange = (index, field, value) => {
        const newComptes = [...(formData.comptesBancaires || [])];
        newComptes[index] = { ...newComptes[index], [field]: value };
        setFormData(prev => ({ ...prev, comptesBancaires: newComptes }));
    };

    const addCompte = () => {
        setFormData(prev => ({
            ...prev,
            comptesBancaires: [...(prev.comptesBancaires || []), {
                rib: '', banqueLogin: '', banquePassword: '', banque: ''
            }]
        }));
    };

    const removeCompte = (index) => {
        const newComptes = [...(formData.comptesBancaires || [])];
        newComptes.splice(index, 1);
        setFormData(prev => ({ ...prev, comptesBancaires: newComptes }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setServerError('');
        try {
            // Préparer les données pour l'envoi de la même manière que dans le formulaire de création
            const dataToSend = {
                ...formData,
                emailSecondaire: formData.emailSecondaire || undefined,
                idRne: formData.idRne || undefined,
                comptesBancaires: (formData.comptesBancaires || [])
                    .filter(compte => compte.rib && compte.rib.trim() !== '') // Ne garder que les comptes avec RIB
                    .map(compte => ({
                        rib: compte.rib ? compte.rib.replace(/\D/g, '') : '',
                        banqueLogin: compte.banqueLogin,
                        banquePassword: compte.banquePassword,
                        banque: compte.banque && compte.banque.trim() !== '' ? compte.banque : undefined
                    }))
            };

            await axios.put(`/api/comptable/clients/${formData._id}`, dataToSend);
            onSuccess();
            onClose();
        } catch (err) {
            setServerError(err.response?.data?.error || 'Erreur lors de la mise à jour');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <form onSubmit={handleSubmit}>
                <DialogTitle>Modifier le client</DialogTitle>
                <DialogContent>
                    <Collapse in={!!serverError}>
                        <Alert severity="error" onClose={() => setServerError('')}>
                            {serverError}
                        </Alert>
                    </Collapse>
                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Type de personne</InputLabel>
                                <Select name="typePersonne" value={formData.typePersonne || ''} onChange={handleChange} label="Type de personne">
                                    <MenuItem value="morale">Personne morale</MenuItem>
                                    <MenuItem value="physique">Personne physique</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {formData.typePersonne === 'morale' && (
                            <Grid item xs={12} sm={6}>
                                <TextField label="Raison sociale" name="raisonSociale" fullWidth value={formData.raisonSociale || ''} onChange={handleChange} />
                            </Grid>
                        )}

                        {formData.typePersonne === 'physique' && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Nom" name="nom" fullWidth value={formData.nom || ''} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="Prénom" name="prenom" fullWidth value={formData.prenom || ''} onChange={handleChange} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField label="CIN" name="cin" fullWidth value={formData.cin || ''} onChange={handleChange} />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField 
                                label="Code client" 
                                name="codeClient" 
                                fullWidth 
                                value={formData.codeClient || ''} 
                                onChange={handleChange} 
                                inputProps={{ maxLength: 7 }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Code TVA" name="codeTva" fullWidth value={formData.codeTva || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Email principal" name="email" fullWidth value={formData.email || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Email secondaire" name="emailSecondaire" fullWidth value={formData.emailSecondaire || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Téléphone" name="telephone" fullWidth value={formData.telephone || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Adresse" name="adresse" fullWidth value={formData.adresse || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Activité" name="activite" fullWidth value={formData.activite || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="ID RNE" name="idRne" fullWidth value={formData.idRne || ''} onChange={handleChange} />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField label="Login TEJ" name="tejLogin" fullWidth value={formData.tejLogin || ''} onChange={handleChange} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Mot de passe TEJ"
                                name="tejPassword"
                                fullWidth
                                type={showTejPassword ? 'text' : 'password'}
                                value={formData.tejPassword || ''}
                                onChange={handleChange}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={() => setShowTejPassword(prev => !prev)}>
                                                {showTejPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>
                    </Grid>

                    <Box mt={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            Comptes Bancaires
                        </Typography>
                        {(formData.comptesBancaires || []).map((compte, index) => (
                            <Grid container spacing={2} key={index} mb={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="RIB"
                                        fullWidth
                                        value={compte.rib || ''}
                                        onChange={(e) => handleCompteChange(index, 'rib', e.target.value)}
                                        placeholder="12345678901234567890"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>Banque (optionnel)</InputLabel>
                                        <Select
                                            value={compte.banque || ''}
                                            onChange={(e) => handleCompteChange(index, 'banque', e.target.value)}
                                            label="Banque (optionnel)"
                                        >
                                            <MenuItem value="">
                                                <em>Sélectionner une banque (optionnel)</em>
                                            </MenuItem>
                                            {banques.map(b => (
                                                <MenuItem key={b._id} value={b._id}>{b.nom}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Login Banque"
                                        fullWidth
                                        value={compte.banqueLogin || ''}
                                        onChange={(e) => handleCompteChange(index, 'banqueLogin', e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Mot de passe Banque"
                                        fullWidth
                                        type={showBanquePasswords[index] ? 'text' : 'password'}
                                        value={compte.banquePassword || ''}
                                        onChange={(e) => handleCompteChange(index, 'banquePassword', e.target.value)}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowBanquePasswords(prev => ({ ...prev, [index]: !prev[index] }))}>
                                                        {showBanquePasswords[index] ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }}
                                    />
                                </Grid>
                                {formData.comptesBancaires && formData.comptesBancaires.length > 1 && (
                                    <Grid item xs={12}>
                                        <Button color="error" onClick={() => removeCompte(index)} startIcon={<RemoveCircle />}>
                                            Supprimer ce compte
                                        </Button>
                                    </Grid>
                                )}
                            </Grid>
                        ))}
                        <Button onClick={addCompte} startIcon={<AddCircle />}>
                            Ajouter un compte bancaire
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>Annuler</Button>
                    <Button type="submit" variant="contained" color="primary" disabled={loading}>
                        {loading ? <CircularProgress size={20} /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default EditClientForm;